// src/pages/page-factory.ts
import {
  SECTION_ONE_VIDEO_URL,
  POSTER_URL,
  BASE_URL,
  type SeoService,
  VIDEO_PUBLICATION_ISO,
  VIDEO_DURATION_SECONDS,
  OG_PRIMARY_IMAGE,
  type ReviewItem,
  DATE_MODIFIED,
  DATE_PUBLISHED,
  serviceByType,
  DEFAULT_OG_IMAGE_ABS,
  TEL_AVIV_OFFICE,
  businessOfficeMapUrl,
  type BusinessOffice,
} from '@/constants/core';

import type {
  AppLang,
  I18nConstants,
} from '@/i18n/locales/i18n.factory';

import {
  globalTypePath,
  typeLocationPath,
  REGIONS,
  type Region,
  type EventType,
  type SeoCity,
  findRegionByCity,
} from '@/constants/geo';

import {
  buildCanonicalUrl,
  buildServicePageGraph,
  stripBrandAnywhere,
  buildBusinessLd,
  buildLogoLd,
  buildWebPageLd,
  buildWebsiteLd,
} from '@/constants/seo-builders';

import { buildAllSectionsJsonLd } from '@/constants/JsonLd';
import { buildBreadcrumbList } from '@/constants/jsonld-utils';

import {
  buildTargetOverrides,
  buildHomeOverrides,
} from '@/constants/buildPages/overrides';
import {
  HOME_SEO_TITLE,
  buildGlobalSeoTitle,
  buildLocalSeoTitle,
} from '@/constants/seo-title-templates';
import { buildGlobalSeoDescription, buildLocalSeoDescription } from '@/constants/seo-description-templates';
import { PLANNING_GUIDE_PATH } from '@/constants/event-planning';

import {
  buildGlobalServiceCityFooterLinks,
  buildInternalLinksForTarget,
  type InternalLink,
} from '@/utils/internalLinks';
import {
  GLOBAL_TYPE_LINK_LANGS,
  getGlobalTypeLabelForLang,
  getGlobalTypePathForLang,
  getLocalizedGlobalTypePath,
} from '@/constants/localized-routes';

// ---------------------
// Types
// ---------------------
export type PageKind =
  | 'home'
  | 'target-global'  // /תקליטן/<type>/
  | 'target-region'  // /תקליטן/<type>/<region>/
  | 'target-city';   // /תקליטן/<type>/<city>/

export type PageConfig = {
  head: {
    title: string;
    description: string;
    canonical: string;
    ogImage: string;
    ogLocale?: string;
    jsonLd?: object[];
    robots: 'index,follow' | 'noindex,follow';
    keywords?: string;
    publisherName?: string;
    authorName?: string;
    ogVideoUrl?: string;
    ogVideoWidth?: number | string;
    ogVideoHeight?: number | string;
  };
  overrides: any;
  internalLinks: {
    section2?: InternalLink[];
    section4?: InternalLink[];
    footer?: InternalLink[];
  };
  serviceForSection5?: SeoService;
};

// ---------------------
// Utils
// ---------------------


const graphNodes = (x: any): object[] =>
  Array.isArray(x?.['@graph']) ? (x['@graph'] as object[]) : [];


const ensureTrailingSlashSafe = (u: string) => {
  const [base, hash = ''] = u.split('#');
  const [path, query = ''] = base.split('?');
  const fixedPath = path.endsWith('/') ? path : `${path}/`;
  return `${fixedPath}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
};

const abs = (p: string) => ensureTrailingSlashSafe(buildCanonicalUrl(p));

const localizedGlobalServiceLinks = (
  i18n: I18nConstants,
  currentType: EventType
): InternalLink[] => {
  if (i18n.HTML_LANG === 'he') return [];

  return (['אירוע', 'חתונה', 'חינה', 'בר-מצווה'] as EventType[])
    .filter((candidateType) => candidateType !== currentType)
    .map((candidateType) => {
      const href = getLocalizedGlobalTypePath(i18n.HTML_LANG, candidateType);
      const label = i18n.preferredKeywordForService(serviceByType[candidateType]);

      return {
        label,
        title: label,
        href: href ? abs(href) : abs(globalTypePath(candidateType)),
      };
    });
};

const englishGlobalServiceLinks = (): InternalLink[] => {
  return (['חתונה', 'חינה', 'בר-מצווה', 'אירוע'] as EventType[])
    .map((type) => {
      const href = getGlobalTypePathForLang('en', type);
      const label = getGlobalTypeLabelForLang('en', type);

      return href
        ? {
            label,
            title: label,
            href: abs(href),
          }
        : null;
    })
    .filter(Boolean) as InternalLink[];
};

const sameTypeLanguageLinks = (
  i18n: I18nConstants,
  currentType: EventType
): InternalLink[] => {
  const currentLang = i18n.HTML_LANG as AppLang;

  return GLOBAL_TYPE_LINK_LANGS
    .filter((lang) => lang !== currentLang)
    .map((lang) => {
      const href = getGlobalTypePathForLang(lang, currentType);
      const label =
        lang === currentLang
          ? i18n.preferredKeywordForService(serviceByType[currentType])
          : getGlobalTypeLabelForLang(lang, currentType);

      return href
        ? {
            label,
            title: label,
            href: abs(href),
          }
        : null;
    })
    .filter(Boolean) as InternalLink[];
};

const siteNodesLd = () => {
  const business = buildBusinessLd({ includeAreaServed: true, includeOpeningHours: true });
  const logo = buildLogoLd();
  return [business, logo];
};

const asRegionMaybe = (label?: string): Region | null => {
  if (!label) return null;
  const l = String(label).trim();
  return (REGIONS as readonly string[]).includes(l) ? (l as Region) : null;
};

const businessOfficeForLocation = (locationLabel?: string): BusinessOffice | undefined => {
  const loc = String(locationLabel ?? '').trim();
  if (!loc) return undefined;

  const region =
    asRegionMaybe(loc) ||
    (typeof findRegionByCity === 'function' ? (findRegionByCity(loc) as Region | null) : null);

  return region === 'צפון' ? TEL_AVIV_OFFICE : undefined;
};

// ---------------------
// HOME
// ---------------------
function buildHome(
  i18n: I18nConstants,
  siteReviews?: ReviewItem[]
): PageConfig {
  const overrides = buildHomeOverrides(
    i18n,
    {
      section6Reviews: Array.isArray(siteReviews)
        ? siteReviews
        : undefined,
      section6ShowAggregate: true,
      section5Links: [
        {
          label: i18n.PLANNING_GUIDE_TITLE,
          href: PLANNING_GUIDE_PATH,
          title: i18n.PLANNING_GUIDE_SUBTITLE,
        },
      ],
    }
  );

  const homeTitle = i18n.HTML_LANG === 'he' ? HOME_SEO_TITLE : i18n.HOME_TITLE;
  const homeUrlWithSlash = `${BASE_URL}/`;
  const websiteLd = buildWebsiteLd();
  const webPageLd = buildWebPageLd({
    name: homeTitle,
    url: homeUrlWithSlash,
    description: i18n.HOME_DESC,
    primaryImage: OG_PRIMARY_IMAGE,
    inLanguage: i18n.HTML_LANG_FULL,
  });
  const breadcrumbLd = buildBreadcrumbList(homeUrlWithSlash, [
    { name: i18n.HTML_LANG === 'he' ? 'בית' : i18n.HOME_TITLE, url: homeUrlWithSlash },
  ]);

  const section4Links: InternalLink[] = (['חתונה', 'בר-מצווה', 'חינה'] as EventType[]).map((type) => {
    const keyword = i18n.preferredKeywordForService(serviceByType[type]);
    return {
      label: keyword,
      href: abs(globalTypePath(type)),
      title: `${keyword} לכל הארץ`,
    };
  });

  const footerLinks: InternalLink[] = [
    ...(['אירוע', 'חתונה', 'חינה', 'בר-מצווה'] as EventType[])
      .flatMap((type) => {
        const keyword = i18n.preferredKeywordForService(serviceByType[type]);

        return REGIONS.map((region) => {
          const regionUi = i18n.translateRegion(region); // ✅ South / Central (ב־EN)
          const isHe = i18n.HTML_LANG === 'he';
          const inLoc = isHe ? `ב${regionUi}` : `in ${regionUi}`;

          const label = `${keyword} ${inLoc}`;
          return {
            label,
            href: abs(typeLocationPath(type, region)), // ✅ URL נשאר SSOT עברי
            title: label,
          };
        });
      }),
    ...englishGlobalServiceLinks(),
  ];

  return {
    head: {
      title: homeTitle,
      description: i18n.HOME_DESC,
      canonical: homeUrlWithSlash,
      ogImage: OG_PRIMARY_IMAGE,
      ogLocale: i18n.HTML_LANG_FULL,
      jsonLd: [
        ...siteNodesLd(),
        websiteLd,
        webPageLd,
        breadcrumbLd,
      ],
      robots: 'index,follow',
      publisherName: i18n.BRAND_TITLE_HE,
      authorName: i18n.BRAND_TITLE_HE,
      keywords: undefined,
      ogVideoUrl: SECTION_ONE_VIDEO_URL,
      ogVideoWidth: 1280,
      ogVideoHeight: 720,
    },
    overrides,
    internalLinks: {
      section4: section4Links,
      footer: footerLinks,
    },
  };
}

// ---------------------
// TARGET – ONE FUNCTION (global + location)
// locationLabel = region OR city (treated same)
// ---------------------
function buildTarget(
  i18n: I18nConstants,
  type: EventType,
  locationLabel?: string,
  siteReviews?: ReviewItem[]
): PageConfig {
  const service: SeoService = serviceByType[type];
  const keyword = i18n.preferredKeywordForService(service);

  const loc = String(locationLabel ?? '').trim(); // slug/SSOT (Hebrew)
  const isGlobal = !loc;
  const businessOffice = businessOfficeForLocation(loc);

  // UI labels
  const locUi = loc ? i18n.translateLocationLabel(loc) : '';
  const isHe = i18n.HTML_LANG === 'he';
  const inLoc = !isGlobal ? (isHe ? `ב${locUi}` : `in ${locUi}`) : '';

  // canonical
  const localizedGlobalPath = isGlobal
    ? getLocalizedGlobalTypePath(i18n.HTML_LANG, type)
    : null;
  const canonicalAbs = isGlobal
    ? abs(localizedGlobalPath ?? globalTypePath(type))
    : abs(typeLocationPath(type, loc)); // path stays SSOT (Hebrew)
  const canonicalWithSlash = ensureTrailingSlashSafe(canonicalAbs);

  // title
  const title = isHe
    ? isGlobal
      ? buildGlobalSeoTitle(service)
      : buildLocalSeoTitle(service, locUi)
    : isGlobal
      ? `${keyword} - ${i18n.BRAND_TITLE_HE}`
      : `${keyword} ${inLoc} - ${i18n.BRAND_TITLE_HE}`;

  // description (FIX: use locUi, not loc, and support EN "in X")
  const description =
    isHe
      ? isGlobal
        ? buildGlobalSeoDescription(service)
        : buildLocalSeoDescription(service, locUi)
      : i18n.makeMetaDescription(service, isGlobal ? undefined : locUi);

  // overrides (locationLabel נכנס כמו "city" ב-overrides — כי זה אותו דבר)
  const overrides = buildTargetOverrides(
    service,
    keyword,
    isGlobal ? '' : locUi, // ✅ UI label per lang (Ashdod / אשדוד)
    i18n,
    {
      section6Reviews: Array.isArray(siteReviews) ? siteReviews : undefined,
      section6ShowAggregate: true,
    }
  );

  // tweak title for global
  if (isGlobal) {
    overrides.section1 = overrides.section1 || {};
    const section1Title = String(overrides.section1.title || keyword).replace(/דיג׳יי/g, 'די ג׳יי');
    overrides.section1.title = `${section1Title} – ${i18n.BRAND_TITLE_SHORT}`;
  }

  if (businessOffice) {
    overrides.section4 = overrides.section4 || {};
    overrides.section4.navigateHref = businessOfficeMapUrl(businessOffice);
  }

  // internal links
  let section4Links: InternalLink[] = [];
  let footerLinks: InternalLink[] = [];

  if (isGlobal) {
    if (isHe) {
      section4Links = (REGIONS as readonly Region[]).map((r) => {
        const rUi = i18n.translateRegion(r);
        const label = `${keyword} ב${rUi}`;
        return {
          label,
          href: abs(typeLocationPath(type, r)),
          title: label,
        };
      });

      footerLinks = buildGlobalServiceCityFooterLinks(i18n as any, { type, service }).map((link) => ({
        ...link,
        href: abs(link.href),
      }));
    } else {
      section4Links = localizedGlobalServiceLinks(i18n, type);
      footerLinks = sameTypeLanguageLinks(i18n, type);
    }
  } else {
    // resolve region cluster: if label is region => use it, else infer from city
    const region: Region =
      asRegionMaybe(loc) ||
      ((typeof findRegionByCity === 'function'
        ? (findRegionByCity(loc) as Region | null)
        : null) || ('מרכז' as Region));

    // cluster links
    const cluster = buildInternalLinksForTarget(i18n as any, {
      region,
      city: loc as any,
      service,
      type,
      maxS4: 6,
      maxFooter: 30,
      includeRegionTarget: true,
    });

    // inject section5/6 links + headings (FIX: bilingual headings)
    overrides.section5 = overrides.section5 || {};
    overrides.section5.links = cluster.section5;
    overrides.section5.linksHeading = isHe ? 'עוד על סוג האירוע' : 'More about this event';

    overrides.section6 = overrides.section6 || { reviews: [] };
    (overrides.section6 as any).links = cluster.section6;
    (overrides.section6 as any).linksHeading = isHe ? 'עוד באזור שלך' : 'More in your area';

    section4Links = cluster.section4;
    footerLinks = cluster.footer;
  }

  // keywords (FIX: in EN don't append Hebrew loc slug blindly)
  const keywords = (() => {
    const variants = (i18n.SEO_KEYWORD_VARIANTS[service] || []).slice(0, 10);
    if (isGlobal) return variants.join(', ');
    if (!variants.length) return undefined;

    // append UI label (not slug) so EN won't get Hebrew junk
    const withLoc = variants.map((v) => `${v} ${locUi}`); // "DJ wedding Ashdod"
    return withLoc.slice(0, 10).join(', ');
  })();

  // LD sections
  const cityForLd = (isGlobal ? '' : loc) as any as SeoCity;

  const sectionsLd = buildAllSectionsJsonLd(service, keyword, cityForLd, overrides, {
    includeVideoRef: true,
    inLanguage: i18n.HTML_LANG_FULL,
    posterUrl: POSTER_URL,
    videoIdAbs: `${canonicalWithSlash}#video`,
    itemReviewedIdAbs: `${String(BASE_URL).replace(/\/$/, '')}#business`,
    pageUrlAbs: canonicalWithSlash,
  });

  const variants = (i18n.SEO_KEYWORD_VARIANTS[service] ?? []) as readonly string[];

  const alternateNames: string[] = isGlobal
    ? Array.from(new Set(variants.filter((v) => v !== keyword))).slice(0, 4)
    : Array.from(
        new Set(
          variants
            .filter((v) => v !== keyword)
            .map((v) => `${v} ${locUi}`) // FIX: use UI loc
        )
      ).slice(0, 4);

  const { ldGraph } = buildServicePageGraph({
    canonicalWithSlash,
    titleNoBrand: stripBrandAnywhere(title),
    fullTitle: title,
    description,
    ogImageAbs: DEFAULT_OG_IMAGE_ABS,
    inLanguage: i18n.HTML_LANG_FULL,
    serviceLabel: keyword,
    cityOrVenue: isGlobal ? '' : locUi, // FIX: UI label for structured data text
    alternateNames,
    datePublished: DATE_PUBLISHED,
    dateModified: DATE_MODIFIED,
    includeVideo: true,
    video: {
      siteBase: String(BASE_URL).replace(/\/$/, ''),
      title,
      videoUrl: SECTION_ONE_VIDEO_URL,
      uploadDate: VIDEO_PUBLICATION_ISO,
      durationISO8601: `PT${VIDEO_DURATION_SECONDS}S`,
    },
    faqItems: [],
    section4Links: section4Links.slice(0, 6).map((l) => ({ href: l.href, label: l.label })),
    includeSiteNodes: true,
    businessOffice,
  });

  // Breadcrumb (FIX: use locUi)
  const breadcrumbItems = isGlobal
    ? [
        { name: isHe ? 'בית' : 'Home', url: abs('/') },
        { name: keyword, url: canonicalWithSlash },
      ]
    : [
        { name: isHe ? 'בית' : 'Home', url: abs('/') },
        { name: keyword, url: abs(globalTypePath(type)) },
        {
          name: isHe ? `${keyword} ב${locUi}` : `${keyword} in ${locUi}`,
          url: canonicalWithSlash,
        },
      ];

  const breadcrumbLd = buildBreadcrumbList(canonicalWithSlash, breadcrumbItems);

  return {
    head: {
      title,
      description,
      canonical: canonicalWithSlash,
      ogImage: OG_PRIMARY_IMAGE,
      ogLocale: i18n.HTML_LANG_FULL,
      jsonLd: [...graphNodes(ldGraph), ...sectionsLd, breadcrumbLd],
      robots: 'index,follow',
      publisherName: i18n.BRAND_TITLE_HE,
      authorName: i18n.BRAND_TITLE_HE,
      keywords,
    },
    overrides,
    internalLinks: {
      section4: section4Links,
      footer: footerLinks,
      section2: [],
    },
    serviceForSection5: service,
  };
}

// ---------------------
// Factory
// ---------------------
export function buildPageConfig(
  kind: PageKind,
  params: {
    lang?: AppLang;
    region?: Region;
    city?: SeoCity;
    type?: EventType;
    siteReviews?: ReviewItem[];
    i18n?: I18nConstants;
  } = {}
): PageConfig {
  const i18n = params.i18n;
  if (!i18n) {
    throw new Error('buildPageConfig requires an i18n instance');
  }

  if (kind === 'home')
    return buildHome(i18n, params.siteReviews);
  
  if (kind === 'target-global' && params.type)
    return buildTarget(
      i18n,
      params.type,
      undefined,
      params.siteReviews
    );
  
  if (kind === 'target-region' && params.type && params.region)
    return buildTarget(
      i18n,
      params.type,
      String(params.region),
      params.siteReviews
    );
  
  if (kind === 'target-city' && params.type && params.city)
    return buildTarget(
      i18n,
      params.type,
      String(params.city),
      params.siteReviews
    );
  
  return buildHome(i18n, params.siteReviews);
}
